<template>
  <FullScreenDialog
    id="updateCEModuleDialog"
    bodyId="updateCEModuleDialogBody"
    fullscreen
    v-model="popupDialog"
    scrollable
  >
    <template #fullscreen-dialog-header>
      <v-btn icon="mdi-close" color="white" variant="text" @click="closeDialog" />
      <v-fade-transition mode="out-in">
        <v-toolbar-title>Update Module</v-toolbar-title>
      </v-fade-transition>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <cs-button
          id="content-update-ce-module-dialog--update-module-btn"
          label="Update"
          @click="update"
          primary
          large
          class="mr-4"
        ></cs-button>
      </v-toolbar-items>
    </template>
    <template #fullscreen-dialog-body>
      <div
        v-if="
          fileDiff &&
          ((fileDiff.added && fileDiff.added.length) ||
            (fileDiff.deleted && fileDiff.deleted.length) ||
            (fileDiff.modified && fileDiff.modified.length))
        "
        class="html-difference"
      >
        <v-row>
          <v-col cols="3">
            <h3 class="mb-0">
              {{ moduleData.displayName ? substituteContextKeyValue(moduleData.displayName) : '' }}
            </h3>
            <div class="module-differences-toc mt-2">
              <v-treeview
                :items="moduleData.toc"
                item-title="name"
                item-key="vpath"
                item-value="vpath"
                expand-icon="mdi-menu-right"
                collapse-icon="mdi-menu-down"
                density="compact"
                tile
              >
                <template v-slot:title="{ item }">
                  <div
                    :class="[
                      'diff-toc-page',
                      'text-link',
                      'text-truncate',
                      getPageDiffStatusClass(item.updateStatus)
                    ]"
                    :id="getNodeId(item)"
                    :title="item && item.name ? substituteContextKeyValue(item.name) : ''"
                    @click="item.updateStatus && showPageDifferences(item)"
                  >
                    {{ item && item.name ? substituteContextKeyValue(item.name) : '' }}
                  </div>
                </template>
              </v-treeview>
            </div>
          </v-col>
          <v-col
            id="content-update-ce-module-dialog--page-differences"
            cols="9"
            v-if="selectedPage"
          >
            <v-row>
              <v-col>
                <h1>Page Differences</h1>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="updates-key">
                <h3>Key</h3>
                <ul>
                  <li>
                    <v-icon class="added-page-color mr-2" icon="mdi-square"></v-icon
                    ><span
                      >Pages highlighted in green in the table of contents are added. Green
                      highlights in the page content indicate that the content has been added.</span
                    >
                  </li>
                  <li>
                    <v-icon class="deleted-page-color mr-2" icon="mdi-square"></v-icon
                    ><span
                      >Pages highlighted in red in the table of contents are deleted. Red highlights
                      in the page content indicate that the content has been removed.</span
                    >
                  </li>
                  <li>
                    <v-icon class="modified-page-color mr-2" icon="mdi-square"></v-icon
                    ><span
                      >Pages highlighted in yellow in the table of contents are changed. Yellow
                      highlights in the page content indicate that the content has been modified or
                      formatted.</span
                    >
                  </li>
                </ul>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <div class="d-flex flex-row align-center justify-space-between">
                  <span
                    >The following changes have been made to this page with this module
                    update.</span
                  >
                  <span>
                    <cs-button
                      id="content-update-ce-module-dialog--return-to-module-diff-summary-btn"
                      class="mb-4 mr-2"
                      label="Return to Update Summary"
                      @click="selectedPage = null"
                      primary
                      large
                    ></cs-button>
                    <cs-button
                      id="content-update-ce-module-dialog--export-page-differences-btn"
                      class="mb-4"
                      label="Export Differences"
                      @click="exportDifferences"
                      primary
                      large
                    ></cs-button>
                  </span>
                </div>
                <v-alert
                  class="mt-2"
                  width="100%"
                  id="content-update-ce-module-dialog--changes-do-not-apply-message"
                  type="info"
                  v-if="
                    selectedPage &&
                    selectedPage.currentContent &&
                    selectedPage.updatedContent &&
                    !selectedPage.updatedContent.includes('vdd-added') &&
                    !selectedPage.updatedContent.includes('vdd-removed') &&
                    !selectedPage.updatedContent.includes('vdd-modified') &&
                    selectedPage.updatedContent !== '<div>No content</div>'
                  "
                >
                  This content has been changed, but does not apply to your site.
                </v-alert>

                <h2 class="mt-4">
                  {{
                    selectedPage && selectedPage.name
                      ? substituteContextKeyValue(selectedPage.name)
                      : ''
                  }}
                </h2>
              </v-col>
            </v-row>
            <v-row>
              <table class="differences-table">
                <thead>
                  <tr>
                    <th>Current User View Content</th>
                    <th>Updated User View Content</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div v-html="selectedPage.currentContent"></div>
                    </td>
                    <td>
                      <div v-html="selectedPage.updatedContent"></div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-row>
          </v-col>
          <v-col cols="9" class="module-update-summary" v-if="!selectedPage">
            <v-row>
              <v-col>
                <h1>Module Update Summary</h1>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="updates-key">
                <h3>Key</h3>
                <ul>
                  <li>
                    <v-icon class="added-page-color mr-2" icon="mdi-square"></v-icon
                    ><span
                      >Pages highlighted in green in the table of contents are added. Green
                      highlights in the page content indicate that the content has been added.</span
                    >
                  </li>
                  <li>
                    <v-icon class="deleted-page-color mr-2" icon="mdi-square"></v-icon
                    ><span
                      >Pages highlighted in red in the table of contents are deleted. Red highlights
                      in the page content indicate that the content has been removed.</span
                    >
                  </li>
                  <li>
                    <v-icon class="modified-page-color mr-2" icon="mdi-square"></v-icon
                    ><span
                      >Pages highlighted in yellow in the table of contents are changed. Yellow
                      highlights in the page content indicate that the content has been modified or
                      formatted.</span
                    >
                  </li>
                </ul>
              </v-col>
            </v-row>
            <v-row v-if="fileDiff && fileDiff.added && fileDiff.added.length">
              <v-col>
                <div class="d-flex flex-nowrap">
                  <h2 class="mr-2">Added Pages</h2>
                  <v-tooltip location="right">
                    <template v-slot:activator="{ props }">
                      <v-icon
                        icon="mdi-information-outline"
                        class="information-outline mb-6"
                        v-bind="props"
                      />
                    </template>
                    <span>This lists all page additions that are included in this update.</span>
                  </v-tooltip>
                </div>

                <div v-for="addedFile in fileDiff.added" :key="addedFile.fileName">
                  <div class="mb-2">
                    <v-icon icon="mdi-file-document" class="added-page-color mr-2" />
                    <span>{{ substituteContextKeyValue(addedFile.pageName) }}</span>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row v-if="fileDiff && fileDiff.deleted && fileDiff.deleted.length">
              <v-col>
                <div class="d-flex flex-nowrap">
                  <h2 class="mr-2">Deleted Pages</h2>
                  <v-tooltip location="right">
                    <template v-slot:activator="{ props }">
                      <v-icon icon="mdi-information-outline" class="mb-6" v-bind="props" />
                    </template>
                    <span>This lists all page deletions that are included in this update.</span>
                  </v-tooltip>
                </div>
                <div v-for="deletedFile in fileDiff.deleted" :key="deletedFile.fileName">
                  <div class="mb-2">
                    <v-icon icon="mdi-file-document" class="deleted-page-color mr-2" />
                    <span>{{ substituteContextKeyValue(deletedFile.pageName) }}</span>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row v-if="fileDiff && fileDiff.modified && fileDiff.modified.length">
              <v-col>
                <div class="d-flex flex-nowrap">
                  <h2 class="mr-2">Changed Pages</h2>
                  <v-tooltip location="right">
                    <template v-slot:activator="{ props }">
                      <v-icon icon="mdi-information-outline" class="mb-6" v-bind="props" />
                    </template>
                    <span
                      >This section shows all content changes in this update, including formatting
                      changes to existing content, added content and deleted content.</span
                    >
                  </v-tooltip>
                </div>
                <div v-for="modifiedFile in fileDiff.modified" :key="modifiedFile.fileName">
                  <div class="mb-2">
                    <v-icon icon="mdi-file-document" class="modified-page-color mr-2" />
                    <span>{{ substituteContextKeyValue(modifiedFile.pageName) }}</span>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </div>

      <cs-form-dialog
        v-model="conflictDialog"
        max-width="500px"
        heading="New Updates"
        :primary-action="{ label: 'OK' }"
        @primary-click="refresh"
      >
        <template v-slot:cs-form-dialog-content>
          <v-container>
            <v-row>
              <span>
                New updates have become available since this page was last loaded. This page will
                refresh with newer information once
                <strong>OK</strong> button is clicked.
              </span>
            </v-row>
          </v-container>
        </template>
      </cs-form-dialog>
    </template>
  </FullScreenDialog>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import FullScreenDialog from '@/components/core/dialog/FullScreenDialog';
import moment from 'moment-timezone';
import { CSBase } from '@complispace/cs-design-system';
import * as types from '@/store/mutationTypes';
import dataManager from '@/lib/engine';
import {
  PUBLISH_TYPE_NORMAL,
  exportPageHTML,
  MIXPANEL_TRACE_EVENT,
  MIXPANEL_TRACE_STRING
} from '@/constants';
import { loadAllImages } from '@/lib/imageRenderUtils';
import { getContextKeySubstitutedTitle } from '@/lib/util';
import plugInAdapter from '@/lib/plugInAdapter';
import { VTreeview } from 'vuetify/labs/VTreeview';
import { publish } from '@/api';
import EventBus from '@/bus';

export default {
  name: 'UpdateCEModuleDialog',
  extends: CSBase,

  props: {
    showDialog: { type: Boolean, required: true, default: false },
    fileDiff: { type: Object, required: true, default: () => {} },
    publishId: { type: Number, required: true },
    moduleId: { type: Number, required: true }
  },

  components: {
    VTreeview,
    FullScreenDialog
  },

  emits: ['closed', 'updateParent'],

  data() {
    return {
      popupDialog: false,
      conflictDialog: false,
      moduleData: {},
      selectedPage: null
    };
  },

  watch: {
    async showDialog(value) {
      if (value) {
        this.popupDialog = true;
        this.conflictDialog = false;
        await this.getModuleData();
      }
    },
    popupDialog(value) {
      if (!value) {
        this.$emit('closed');
      }
    }
  },

  computed: {
    ...mapState({
      client: (state) => state.client,
      subdomain: (state) => state.session.subdomain
    }),
    ...mapGetters({
      appliedUpdateItems: 'published/appliedItems',
      activatedCEModules: 'activatedCEModules/items',
      contextValues: 'clientContext/values',
      appName: 'app/name'
    })
  },

  methods: {
    ...mapMutations({
      setPageCommitOID: types.DATA_SET_PAGE_COMMIT_OID,
      setPageCommitTimestamp: types.DATA_SET_PAGE_COMMIT_TIMESTAMP,
      setDataUpdatedModulesState: types.DATA_SET_UNPUBLISHED_UPDATES_STATE
    }),

    async update() {
      if (this.loading) return;
      this.setLoading(true);
      const moduleUpdateItems = [];
      if (!!this.appliedUpdateItems && this.appliedUpdateItems.length > 0) {
        this.appliedUpdateItems.forEach((update) => {
          if (update.moduleId === this.moduleId) {
            const foundModule = this.activatedCEModules.find(
              (activatedCEModules) => activatedCEModules.id === update.moduleId
            );
            // check if clientId where CS module = null or undefined, include
            if (
              !!foundModule &&
              (foundModule.clientId === null || foundModule.clientId === undefined)
            ) {
              moduleUpdateItems.push(update);
            }
          }
        });
      }
      try {
        const response = await publish.merge(this.publishId, this.moduleId);

        const { hasConflicts } = response.data;
        // Fetch the context keys again - there might be new ones
        // from the update
        if (!hasConflicts) {
          await this.$store.dispatch('clientContext/getAll');

          this.trackMixPanelAcceptedUpdatesEvent(moduleUpdateItems);

          // TODO: the following may be removed in the future with further verification
          // $emit('updateParent') will also call the following two

          await this.$store.dispatch('published/getAllAppliedUpdates');

          await this.$store.dispatch('published/getAllComplispaceUpdates');

          // Reset pageCommitOID and pageCommitTimestamp, which are used to indicate that we are viewing a previous version of content corresponding to the commit OID.
          // When we accept the latest update, we should only see the latest content version with latest context key values.
          this.setPageCommitOID('');
          this.setPageCommitTimestamp('');
          this.setDataUpdatedModulesState(dataManager.MANAGER_STATE.STATE_IDLE);
        }

        this.$emit('updateParent');
        EventBus.$emit('updateSideNavigation');
        this.popupDialog = hasConflicts;
      } catch (err) {
        if (err.response && err.response.status === 409) {
          this.conflictDialog = true;
        } else {
          this.showErrorAlert(err.message);
        }
      } finally {
        this.setLoading(false);
        this.popupDialog = this.conflictDialog;
      }
    },
    trackMixPanelAcceptedUpdatesEvent(moduleUpdateItems) {
      try {
        if (!!moduleUpdateItems && moduleUpdateItems.length > 0) {
          // eslint-disable-next-line no-underscore-dangle
          const { plugIns } = this.$app;

          const currentDate = moment(new Date()).format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]');

          const commonMixpanelProperties = {
            appName: this.appName,
            client: this.client,
            subdomain: this.subdomain,
            userRole: MIXPANEL_TRACE_STRING.USER_ROLE_VIEW.ADMIN
          };

          moduleUpdateItems.forEach((update) => {
            if (update.typeId === PUBLISH_TYPE_NORMAL) {
              const pendingUpdatesProperties = {
                publishId: update.publishId,
                publishSummary: update.publishSummary,
                typeId: update.typeId,
                publishAt: currentDate,
                moduleId: update.moduleId,
                moduleName: update.moduleName
              };

              const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({
                ...commonMixpanelProperties,
                ...pendingUpdatesProperties
              });

              plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.ACCEPTED_UPDATES, properties);
            }
          });
        }
      } catch (error) {
        console.log('Mixpanel Error: ', error);
      }
    },

    refresh() {
      window.location.reload();
    },

    async getModuleData() {
      let activatedModule = null;

      if (this.activatedCEModules && this.activatedCEModules.length) {
        activatedModule = this.activatedCEModules.find((module) => module.id === this.moduleId);
      }

      const formattedToc = this.updateTocJsonProperties(this.fileDiff.mergedToc);
      this.moduleData = { ...activatedModule, toc: [formattedToc] };
    },

    updateTocJsonProperties(toc) {
      if (this.fileDiff.added.find((addedPage) => addedPage.fileName === toc.path)) {
        // eslint-disable-next-line no-param-reassign
        toc.updateStatus = 'added';
      }

      if (this.fileDiff.deleted.find((deletedPage) => deletedPage.fileName === toc.path)) {
        // eslint-disable-next-line no-param-reassign
        toc.updateStatus = 'deleted';
      }

      if (this.fileDiff.modified.find((modifiedPage) => modifiedPage.fileName === toc.path)) {
        // eslint-disable-next-line no-param-reassign
        toc.updateStatus = 'modified';
      }

      if (toc.children && toc.children.length) {
        toc.children.forEach((child) => {
          this.updateTocJsonProperties(child);
        });
      } else {
        // eslint-disable-next-line no-param-reassign
        toc.children = undefined; // add else block to set toc.children as undefined instead of leaving it as empty array if has no children
      }
      return toc;
    },

    getNodeId(item) {
      const path = item && item.path ? item.path.substring(0, item.path.length - 3) : '';
      return `toc-node-${this.moduleId}-${path}`;
    },

    getPageDiffStatusClass(status) {
      let diffClass;
      switch (status) {
        case 'added':
          diffClass = 'added-page';
          break;

        case 'deleted':
          diffClass = 'deleted-page';
          break;

        case 'modified':
          diffClass = 'modified-page';
          break;

        default:
          diffClass = '';
          break;
      }
      return diffClass;
    },

    async showPageDifferences(item) {
      try {
        const response = await publish.getPageDiff(this.publishId, item.path);
        const currentContent =
          response.status === 200 && response.data && response.data.originalContent.length
            ? response.data.originalContent
            : '<div>No content</div>';

        let updatedContent = '<div>No content</div>';

        if (
          response.status === 200 &&
          response.data &&
          response.data.differences.length &&
          item.updateStatus !== 'deleted'
        ) {
          updatedContent = response.data.differences;
        }

        this.selectedPage = { ...item, currentContent, updatedContent };
      } catch (err) {
        this.selectedPage = {
          ...item,
          currentContent: '<div>No content</div>',
          updatedContent: '<div>No content</div>'
        };
        this.showErrorAlert(err.message);
      }
    },

    substituteContextKeyValue(text) {
      return getContextKeySubstitutedTitle(text, this.contextValues);
    },

    async exportDifferences() {
      const differences = document.getElementById(
        'content-update-ce-module-dialog--page-differences'
      );

      const filename = this.selectedPage.name || 'Print View';
      const winPrint = window.open('', filename, 'toolbar=0,titlebar=1,scrollbars=1,status=0');
      moment.tz.setDefault('Australia/Sydney');
      const currentDate = moment(new Date()).format('DD/MM/YYYY HH:mm');

      const exportedHTML = exportPageHTML(
        currentDate,
        currentDate,
        filename,
        this.client.logoUri,
        differences.innerHTML
      );

      winPrint.document.write(exportedHTML);
      await loadAllImages(winPrint.document);
      winPrint.document.close();
    },

    closeDialog() {
      this.selectedPage = null;
      this.popupDialog = false;
    }
  }
};
</script>

<style scoped>
.html-difference :deep(.vdd-added) {
  background-color: #c8e6c9;
}

.html-difference :deep(.vdd-removed) {
  background-color: #ffcdd2;
}

.html-difference :deep(.vdd-modified) {
  background-color: #ffecb3;
}

.html-difference :deep(ins),
.html-difference :deep(del) {
  text-decoration: none;
}

.html-difference :deep(.v-card__text div > h1:first-child),
.html-difference :deep(.v-card__text div > h2:first-child),
.html-difference :deep(.v-card__text div > h3:first-child),
.html-difference :deep(.v-card__text div > h4:first-child),
.html-difference :deep(.v-card__text div > h5:first-child),
.html-difference :deep(.v-card__text div > h6:first-child) {
  font-size: 24px;
  font-weight: 400;
  color: #555757;
  line-height: 32px;
  letter-spacing: normal;
  margin-bottom: 2rem;
}

.html-difference :deep(h1),
.html-difference :deep(h2),
.html-difference :deep(h3) {
  margin-bottom: 1.5rem;
  color: rgba(0, 0, 0, 0.6);
}

.html-difference :deep(.v-btn__content .v-icon),
.html-difference :deep(.information-outline) {
  color: rgba(0, 0, 0, 0.6) !important;
}

.html-difference :deep(h4),
.html-difference :deep(h5),
.html-difference :deep(h6) {
  margin-bottom: 1rem;
}
.html-difference :deep(span.underline) {
  text-decoration: underline !important;
}

.html-difference :deep(.pm-table-wrapper) {
  overflow: auto;
}

.html-difference :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 1rem;
}

.html-difference :deep(td),
div :deep(th) {
  border: 1px solid #ddd;
  padding: 1rem;
  vertical-align: top;
}

.html-difference :deep(.pm-table-wrapper td),
.html-difference :deep(.pm-table-wrapper th) {
  word-break: break-word;
}

.html-difference :deep(th) {
  text-align: left;
  background-color: #f2f2f2;
  color: black;
}

.html-difference :deep(img) {
  display: block;
  margin: 0 auto;
  max-width: 80%;
  width: auto !important;
  height: auto !important;
}

.html-difference :deep(pre) {
  padding: 0.7rem 1rem;
  border-radius: 5px;
  background: black;
  color: white;
  font-size: 0.8rem;
  overflow-x: auto;
}

.html-difference :deep(pre code) {
  display: block;
}

.v-application .html-difference :deep(code) {
  display: inline-block;
  padding: 0 0.4rem;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: bold;
  background: rgba(0, 0, 0, 0.1);
  color: inherit;
}

.html-difference :deep(blockquote) {
  border-left: 3px solid rgba(0, 0, 0, 0.1);
  padding-left: 0.8rem;
  font-style: italic;
}

.html-difference :deep(ol ol) {
  list-style-type: lower-alpha;
}

.html-difference :deep(ol ol ol) {
  list-style-type: lower-roman;
}

.html-difference :deep(ul),
.html-difference :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 2rem !important;
}

.html-difference :deep(li p) {
  margin: 0;
}

.html-difference li > ul,
.html-difference li > ol {
  margin-bottom: auto;
}

.module-differences-toc :deep(.added-page) {
  background-color: #c8e6c9;
  cursor: pointer !important;
}

.module-differences-toc :deep(.deleted-page) {
  background-color: #ffcdd2;
  cursor: pointer !important;
}

.module-differences-toc :deep(.modified-page) {
  background-color: #ffecb3;
  cursor: pointer !important;
}

.module-update-summary :deep(.added-page-color),
.html-difference :deep(.added-page-color) {
  color: #c8e6c9;
}

.module-update-summary :deep(.deleted-page-color),
.html-difference :deep(.deleted-page-color) {
  color: #ffcdd2;
}

.module-update-summary :deep(.modified-page-color),
.html-difference :deep(.modified-page-color) {
  color: #ffecb3;
}

.module-differences-toc :deep(.diff-toc-page) {
  padding: 0.5rem;
  border-radius: 5px;
  cursor: default;
}

.module-update-summary .updates-key,
.html-difference .updates-key {
  border: 1px solid rgb(var(--v-theme-secondary));
  border-radius: 5px;
  margin-bottom: 1.5rem;
}

.module-update-summary .updates-key ul,
.html-difference .updates-key ul {
  list-style-type: none;
  padding-left: 0 !important;
}

.module-update-summary .updates-key ul,
.html-difference table.differences-table th {
  width: 50%;
}

/* fix indentation limitation on vuetify treeview - https://github.com/vuetifyjs/vuetify/issues/20421 */
.html-difference
  :deep(
    .v-list-item.v-list-item--link.v-list-item--density-compact.v-treeview-item:not(
        .v-list-group__header
      )
  ) {
  padding-inline-start: calc(44px + var(--indent-padding)) !important;
}
</style>
